import {
  cuisineNamesFromSelection,
  cuisinesFromLegacyNames,
} from "../../services/cuisineCacheService";

export const FIND_JOBS_LIMIT = 10;
export const FIND_JOBS_SALARY_MIN = 5000;
export const FIND_JOBS_SALARY_MAX = 100000;
export const FIND_JOBS_MAX_SKIP = 200;
export const FIND_JOBS_MAX_GEO_RADIUS_TIER = 3;
export const FIND_JOBS_GEO_RADIUS_KM = [100, 250, 500];
export const SHORTLIST_DEBOUNCE_MS = 450;

/** employerId.propertyType — hide when null or Others (5). */
export const ESTABLISHMENT_TYPE_LABELS = {
  1: "Hotel / Resort",
  2: "Restaurant",
  3: "Cafe / Bistro",
  4: "Cloud Kitchen",
  6: "Food Truck / Cart",
};

export function getEstablishmentTypeLabel(propertyType) {
  if (propertyType == null || propertyType === "" || Number(propertyType) === 5) {
    return null;
  }
  return ESTABLISHMENT_TYPE_LABELS[Number(propertyType)] || null;
}

export function isChefJobAlreadyShortlistedError(error) {
  const message = String(
    error?.message || error?.response?.data?.message || ""
  )
    .trim()
    .toLowerCase();

  return message.includes("already shortlisted");
}

export function normalizeFindJobsListItem(job) {
  if (!job || typeof job !== "object") {
    return job;
  }

  return {
    ...job,
    isViewed: job.isViewed === true,
    isShortlisted: job.isShortlisted === true,
    viewsCount: job.viewsCount ?? job.jobViewsCount ?? 0,
  };
}

export function parseFindJobsResponse(raw) {
  if (!raw || typeof raw !== "object") {
    return {
      jobs: [],
      hasMore: false,
      canExpandRadius: false,
      nextSkip: 0,
      maxSkip: FIND_JOBS_MAX_SKIP,
      geoRadiusTier: 1,
      geoRadiusKm: null,
      maxGeoRadiusTier: FIND_JOBS_MAX_GEO_RADIUS_TIER,
      isNew: false,
    };
  }

  const jobs = Array.isArray(raw.data)
    ? raw.data.map(normalizeFindJobsListItem)
    : [];

  const nextSkip = raw.nextSkip ?? jobs.length;
  const maxSkip = raw.maxSkip ?? FIND_JOBS_MAX_SKIP;

  return {
    jobs,
    hasMore: Boolean(raw.hasMore) && nextSkip <= maxSkip,
    canExpandRadius: Boolean(raw.canExpandRadius),
    nextSkip,
    maxSkip,
    geoRadiusTier: raw.geoRadiusTier ?? 1,
    geoRadiusKm: raw.geoRadiusKm ?? null,
    maxGeoRadiusTier: raw.maxGeoRadiusTier ?? FIND_JOBS_MAX_GEO_RADIUS_TIER,
    isNew: Boolean(raw.isNew),
  };
}

export function getFindJobsRadiusKmForTier(tier = 1) {
  const safeTier = Math.max(1, Math.min(tier, FIND_JOBS_GEO_RADIUS_KM.length));
  return FIND_JOBS_GEO_RADIUS_KM[safeTier - 1];
}

export function getFindJobsNextRadiusKm(currentTier = 1) {
  if (currentTier >= FIND_JOBS_MAX_GEO_RADIUS_TIER) return null;
  return FIND_JOBS_GEO_RADIUS_KM[currentTier];
}

export function getFindJobsLoadMoreLabel({
  hasMore,
  canExpandRadius,
  geoRadiusTier = 1,
  isLoadingMore = false,
}) {
  if (isLoadingMore) return "Loading...";
  if (hasMore) return "Show more";

  const nextKm = getFindJobsNextRadiusKm(geoRadiusTier);
  if (canExpandRadius && nextKm) {
    return `Search within ${nextKm} km`;
  }
  if (canExpandRadius) return "Search wider area";
  return "Show more";
}

export function buildFindJobsPayload({
  skip = 0,
  geoRadiusTier = 1,
  excludeJobIds = [],
  selectedLocation = {},
  cuisines = [],
  minimumExperience,
  maximumExperience,
  minSalary = FIND_JOBS_SALARY_MIN,
  maxSalary = FIND_JOBS_SALARY_MAX,
  sortingBySalary,
  sortingByCreatedAt,
  limit = FIND_JOBS_LIMIT,
} = {}) {
  const payload = {
    limit,
    skip,
    geoRadiusTier,
  };

  if (Array.isArray(excludeJobIds) && excludeJobIds.length > 0) {
    payload.excludeJobIds = excludeJobIds.map(String).filter(Boolean);
  }

  const { lat, lng } = selectedLocation;
  if (lat != null && lng != null && !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lng))) {
    payload.latitude = Number(lat);
    payload.longitude = Number(lng);
  }

  const cuisineNames = Array.isArray(cuisines)
    ? cuisines.every((item) => typeof item === "string")
      ? cuisines
      : cuisineNamesFromSelection(cuisines)
    : [];

  if (cuisineNames.length > 0) {
    payload.cuisines = cuisineNames;
  }

  if (minSalary != null && minSalary !== "") {
    payload.minimumSalary = Number(minSalary);
  }
  if (maxSalary != null && maxSalary !== "") {
    payload.maximumSalary = Number(maxSalary);
  }

  const hasMinExp =
    minimumExperience != null &&
    minimumExperience !== "" &&
    !Number.isNaN(Number(minimumExperience));
  const hasMaxExp =
    maximumExperience != null &&
    maximumExperience !== "" &&
    !Number.isNaN(Number(maximumExperience));

  // Experience filter applies only when both bounds are present
  if (hasMinExp && hasMaxExp) {
    payload.minimumExperience = Number(minimumExperience);
    payload.maximumExperience = Number(maximumExperience);
  }

  if (sortingBySalary != null) {
    payload.sortingBySalary = sortingBySalary;
  } else if (sortingByCreatedAt != null) {
    payload.sortingByCreatedAt = sortingByCreatedAt;
  }

  return payload;
}

export function isFindJobsLocationValid(locationLabel, selectedLocation) {
  const trimmed = String(locationLabel || "").trim();
  if (!trimmed) return true;
  return (
    selectedLocation?.lat != null &&
    selectedLocation?.lng != null &&
    !Number.isNaN(Number(selectedLocation.lat)) &&
    !Number.isNaN(Number(selectedLocation.lng))
  );
}

export function formatJobSalary(value, maxSalary) {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);

  const formattedMin = `₹${num.toLocaleString("en-IN")}`;
  if (maxSalary == null || maxSalary === "") return formattedMin;

  const maxNum = Number(maxSalary);
  if (Number.isNaN(maxNum) || maxNum <= num) return formattedMin;

  return `${formattedMin} – ₹${maxNum.toLocaleString("en-IN")}`;
}

export function sortByFromQuery(sortby) {
  if (sortby === "salary-high-low") return { sortingBySalary: -1 };
  if (sortby === "salary-low-high") return { sortingBySalary: 1 };
  if (sortby === "date-desc-asc") return { sortingByCreatedAt: -1 };
  if (sortby === "date-asc-desc") return { sortingByCreatedAt: 1 };
  return {};
}

export function experienceRangeFromQuery(minExp, maxExp) {
  if (minExp == null || minExp === "") {
    return { minimumExperience: undefined, maximumExperience: undefined, exp: "" };
  }

  const min = parseInt(minExp, 10);
  const max = parseInt(maxExp, 10);

  if (Number.isNaN(min)) {
    return { minimumExperience: undefined, maximumExperience: undefined, exp: "" };
  }

  const exp =
    min === 0 && max === 2
      ? "0-2"
      : min === 2 && max === 5
        ? "2-5"
        : min === 5 && max === 8
          ? "5-8"
          : min === 8 && max === 10
            ? "8-10"
            : min === 10 && max === 15
              ? "10-15"
              : min === 15 && max === 25
                ? "15-25"
                : min === 25 && max === 50
                  ? "25-50"
                  : `${min}-${max}`;

  return {
    minimumExperience: min,
    maximumExperience: Number.isNaN(max) ? undefined : max,
    exp,
  };
}

export function readFindJobsFiltersFromSearchParams(searchParams) {
  const cuisineval =
    searchParams.get("cuisines")?.split(",").filter(Boolean) ?? [];
  const minexpval = searchParams.get("minimumexp");
  const maxexpval = searchParams.get("maximumexp");
  const minsal = searchParams.get("minimumSalary");
  const maxsal = searchParams.get("maximumSalary");
  const locationq = searchParams.get("location") || "";
  const latQ = searchParams.get("lat");
  const lngQ = searchParams.get("lng");
  const sortbyQ = searchParams.get("sortby") || "";
  const expRange = experienceRangeFromQuery(minexpval, maxexpval);
  const sortFields = sortByFromQuery(sortbyQ);

  const selectedLocation =
    latQ != null && lngQ != null
      ? {
          lat: Number(latQ),
          lng: Number(lngQ),
          placeId: null,
          locationScope: "city",
          locationType: null,
        }
      : {
          lat: null,
          lng: null,
          placeId: null,
          locationScope: "city",
          locationType: null,
        };

  return {
    selectedCuisines: cuisineval.length ? cuisinesFromLegacyNames(cuisineval) : [],
    locationLabel: locationq,
    selectedLocation,
    minSalary: minsal ? parseInt(minsal, 10) : FIND_JOBS_SALARY_MIN,
    maxSalary: maxsal ? parseInt(maxsal, 10) : FIND_JOBS_SALARY_MAX,
    minimumExperience: expRange.minimumExperience,
    maximumExperience: expRange.maximumExperience,
    exp: expRange.exp,
    sortby: sortbyQ,
    sortingBySalary: sortFields.sortingBySalary,
    sortingByCreatedAt: sortFields.sortingByCreatedAt,
  };
}

export function buildFindJobsPayloadFromFilters(filters, pagination = {}) {
  return buildFindJobsPayload({
    skip: pagination.skip ?? 0,
    geoRadiusTier: pagination.geoRadiusTier ?? 1,
    excludeJobIds: pagination.excludeJobIds ?? [],
    selectedLocation: filters.selectedLocation,
    cuisines: filters.selectedCuisines,
    minimumExperience: filters.minimumExperience,
    maximumExperience: filters.maximumExperience,
    minSalary: filters.minSalary,
    maxSalary: filters.maxSalary,
    sortingBySalary: filters.sortingBySalary,
    sortingByCreatedAt: filters.sortingByCreatedAt,
  });
}

export function experienceRangeFromSelect(value) {
  if (!value) {
    return { minimumExperience: undefined, maximumExperience: undefined };
  }

  const [minExp, maxExp] = value.split("-");
  return {
    minimumExperience: parseInt(minExp, 10),
    maximumExperience: parseInt(maxExp, 10),
  };
}
